import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SuperAdminRoute } from "./components/SuperAdminRoute";
import { Loader2, AlertCircle } from "lucide-react";

import CookieConsent from "./components/CookieConsent";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { db, isFirebaseConfigured } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { Analytics } from "@vercel/analytics/react";

// Eager-loaded pages (critical path)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";

// Lazy-loaded public pages
const Jobs = lazy(() => import("./pages/Jobs"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ForEmployers = lazy(() => import("./pages/ForEmployers"));
const Login = lazy(() => import("./pages/Login"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AcceptInvite = lazy(() => import("./pages/auth/AcceptInvite"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const VideoCall = lazy(() => import("./pages/VideoCall"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const PublicOfferView = lazy(() => import("./pages/PublicOfferView"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const Status = lazy(() => import("./pages/Status"));

// Lazy-loaded candidate pages
const CandidateOnboarding = lazy(() => import("./pages/candidate/Onboarding"));
const CandidateDashboard = lazy(() => import("./pages/candidate/Dashboard"));
const CandidateInterview = lazy(() => import("./pages/candidate/Interview"));
const CandidateProfile = lazy(() => import("./pages/candidate/Profile"));
const CandidateJobs = lazy(() => import("./pages/candidate/Jobs"));
const CandidateJobDetails = lazy(() => import("./pages/candidate/JobDetails"));
const CandidateApplications = lazy(() => import("./pages/candidate/Applications"));
const ApplicationDetail = lazy(() => import("./pages/candidate/ApplicationDetail"));
const CandidateCalendar = lazy(() => import("./pages/candidate/Calendar"));
const CandidateInterviews = lazy(() => import("./pages/candidate/Interviews"));
const CandidateSettings = lazy(() => import("./pages/candidate/Settings"));
const CandidateDocuments = lazy(() => import("./pages/candidate/Documents"));
const VoiceOnboarding = lazy(() => import("./pages/candidate/VoiceOnboarding"));
const CandidateQuickApply = lazy(() => import("./pages/candidate/QuickApply"));


// Lazy-loaded employer pages
const EmployerOnboarding = lazy(() => import("./pages/employer/Onboarding"));
const EmployerAwaitingApproval = lazy(() => import("./pages/employer/AwaitingApproval"));
const EmployerDashboard = lazy(() => import("./pages/employer/Dashboard"));
const PostJob = lazy(() => import("./pages/employer/PostJob"));
const ScheduleInterview = lazy(() => import("./pages/employer/Schedule"));
const EmployerCandidates = lazy(() => import("./pages/employer/Candidates"));
const CandidateDetail = lazy(() => import("./pages/employer/CandidateDetail"));
const EmployerJobs = lazy(() => import("./pages/employer/Jobs"));
const EmployerJobDetails = lazy(() => import("./pages/employer/JobDetails"));
const EmployerInterviews = lazy(() => import("./pages/employer/Interviews"));
const EmployerAnalytics = lazy(() => import("./pages/employer/Analytics"));
const EmployerSettings = lazy(() => import("./pages/employer/Settings"));
const OfferLetters = lazy(() => import("./pages/employer/OfferLetters"));
const TalentPool = lazy(() => import("./pages/employer/TalentPool"));
const Automations = lazy(() => import("./pages/employer/Automations"));
const Documents = lazy(() => import("./pages/employer/Documents"));
const EmployerProfile = lazy(() => import("./pages/employer/Profile"));
const AgentCommandCenter = lazy(() => import("./pages/employer/AgentCommandCenter"));
const EmployeeDirectory = lazy(() => import("./pages/employer/EmployeeDirectory"));
const EmployeeProfile = lazy(() => import("./pages/employer/EmployeeProfile"));
const Organization = lazy(() => import("./pages/employer/Organization"));
const DepartmentDetail = lazy(() => import("./pages/employer/DepartmentDetail"));
const TeamDetail = lazy(() => import("./pages/employer/TeamDetail"));
const OnboardingManagement = lazy(() => import("./pages/employer/OnboardingManagement"));
const TaskDetail = lazy(() => import("./pages/employer/TaskDetail"));
const CandidateOnboardingTasks = lazy(() => import("./pages/candidate/CandidateOnboardingTasks"));

const PageLoader = () => (
  <div aria-label="loading application" className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-accent" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,   // 2 minutes before refetch
      gcTime: 10 * 60 * 1000,     // 10 minutes in cache
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const DashboardRedirect = () => {
  const { user, activeRole, loading, getUserRole } = useAuth();
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    const resolveAndCheck = async () => {
      if (!user?.id) return;
      setIsResolving(true);
      try {
        // 1. Ensure activeRole is set
        let currentRole = activeRole;
        if (!currentRole) {
          // Resolving missing role...
          currentRole = await getUserRole();
        }

        // 2. Check profile completion
        const profileSnap = await getDoc(doc(db, 'profiles', user.id));
        if (profileSnap.exists()) {
          setProfileCompleted(profileSnap.data().profile_completed ?? false);
        } else {
          setProfileCompleted(false);
        }
      } catch (err) {
        console.error("DashboardRedirect: Check failed", err);
        setProfileCompleted(false);
      } finally {
        setIsResolving(false);
      }
    };

    if (!loading && user) {
      resolveAndCheck();
    } else if (!loading && !user) {
      setProfileCompleted(true); // Allow redirect to /auth
    }
  }, [user, loading, activeRole, getUserRole]);

  if (loading || (user && (profileCompleted === null || isResolving))) {
    return <PageLoader />;
  }

  if (!user) return <Navigate to="/auth" replace />;

  // Default to candidate if role resolution failed but we have a user
  const effectiveRole = activeRole || 'candidate';

  if (effectiveRole === 'candidate' && !profileCompleted) {
    return <Navigate to="/candidate/onboarding" replace />;
  }

  return <Navigate to={`/${effectiveRole}/dashboard`} replace />;
};

const App = () => (
  <HelmetProvider>
    <Analytics />
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* Firebase Config Warning Banner (dev/staging only) */}
        {!isFirebaseConfigured && (
          <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Firebase credentials not configured — add VITE_FIREBASE_* to your .env file to enable authentication and data features.
          </div>
        )}
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/dashboard" element={<DashboardRedirect />} />
                <Route path="/null/dashboard" element={<Navigate to="/dashboard" replace />} />
                
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/get-started" element={<GetStarted />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/:jobId" element={<JobDetail />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/for-employers" element={<ForEmployers />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/invite" element={<AcceptInvite />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/shipping-policy" element={<ShippingPolicy />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/case-studies" element={<CaseStudies />} />
                <Route path="/status" element={<Status />} />
                <Route path="/video-call/:eventId" element={<VideoCall />} />
                <Route path="/offer/:offerId" element={<PublicOfferView />} />

                {/* Candidate Routes */}
                <Route path="/candidate/onboarding" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateOnboarding /></ProtectedRoute>
                } />
                <Route path="/candidate/onboarding/voice" element={
                  <ProtectedRoute requiredRole="candidate"><VoiceOnboarding /></ProtectedRoute>
                } />
                <Route path="/candidate/dashboard" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateDashboard /></ProtectedRoute>
                } />
                <Route path="/candidate/interview" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateInterview /></ProtectedRoute>
                } />
                <Route path="/candidate/profile" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateProfile /></ProtectedRoute>
                } />
                <Route path="/candidate/jobs" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateJobs /></ProtectedRoute>
                } />
                <Route path="/candidate/jobs/:jobId" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateJobDetails /></ProtectedRoute>
                } />
                <Route path="/candidate/quick-apply/:jobId" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateQuickApply /></ProtectedRoute>
                } />
                <Route path="/candidate/applications" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateApplications /></ProtectedRoute>
                } />
                <Route path="/candidate/applications/:applicationId" element={
                  <ProtectedRoute requiredRole="candidate"><ApplicationDetail /></ProtectedRoute>
                } />
                <Route path="/candidate/interviews" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateInterviews /></ProtectedRoute>
                } />
                <Route path="/candidate/calendar" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateCalendar /></ProtectedRoute>
                } />
                <Route path="/candidate/settings" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateSettings /></ProtectedRoute>
                } />
                <Route path="/candidate/documents" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateDocuments /></ProtectedRoute>
                } />
                <Route path="/candidate/my-onboarding" element={
                  <ProtectedRoute requiredRole="candidate"><CandidateOnboardingTasks /></ProtectedRoute>
                } />


                {/* Employer Routes */}
                {/* HRMS Onboarding (more specific route must come first) */}
                <Route path="/employer/onboarding-tasks/:taskId" element={
                  <ProtectedRoute requiredRole="employer"><TaskDetail /></ProtectedRoute>
                } />
                <Route path="/employer/onboarding-tasks" element={
                  <ProtectedRoute requiredRole="employer"><OnboardingManagement /></ProtectedRoute>
                } />
                {/* Employer Company Onboarding */}
                <Route path="/employer/onboarding" element={
                  <ProtectedRoute requiredRole="employer"><EmployerOnboarding /></ProtectedRoute>
                } />
                <Route path="/employer/access-pending" element={
                  <ProtectedRoute requiredRole="employer"><EmployerAwaitingApproval /></ProtectedRoute>
                } />
                <Route path="/employer/dashboard" element={
                  <ProtectedRoute requiredRole="employer"><EmployerDashboard /></ProtectedRoute>
                } />
                <Route path="/employer/jobs/new" element={
                  <ProtectedRoute requiredRole="employer"><PostJob /></ProtectedRoute>
                } />
                <Route path="/employer/jobs" element={
                  <ProtectedRoute requiredRole="employer"><EmployerJobs /></ProtectedRoute>
                } />
                <Route path="/employer/jobs/:jobId" element={
                  <ProtectedRoute requiredRole="employer"><EmployerJobDetails /></ProtectedRoute>
                } />
                <Route path="/employer/schedule" element={
                  <ProtectedRoute requiredRole="employer"><ScheduleInterview /></ProtectedRoute>
                } />
                <Route path="/employer/candidates" element={
                  <ProtectedRoute requiredRole="employer"><EmployerCandidates /></ProtectedRoute>
                } />
                <Route path="/employer/candidates/:candidateId" element={
                  <ProtectedRoute requiredRole="employer"><CandidateDetail /></ProtectedRoute>
                } />
                <Route path="/employer/interviews" element={
                  <ProtectedRoute requiredRole="employer"><EmployerInterviews /></ProtectedRoute>
                } />
                <Route path="/employer/analytics" element={
                  <ProtectedRoute requiredRole="employer"><EmployerAnalytics /></ProtectedRoute>
                } />
                <Route path="/employer/settings" element={
                  <ProtectedRoute requiredRole="employer"><EmployerSettings /></ProtectedRoute>
                } />
                <Route path="/employer/offer-letters" element={
                  <ProtectedRoute requiredRole="employer"><OfferLetters /></ProtectedRoute>
                } />
                <Route path="/employer/talent-pool" element={
                  <ProtectedRoute requiredRole="employer"><TalentPool /></ProtectedRoute>
                } />
                <Route path="/employer/employees" element={
                  <ProtectedRoute requiredRole="employer"><EmployeeDirectory /></ProtectedRoute>
                } />
                <Route path="/employer/employees/:employeeId" element={
                  <ProtectedRoute requiredRole="employer"><EmployeeProfile /></ProtectedRoute>
                } />
                <Route path="/employer/organization" element={
                  <ProtectedRoute requiredRole="employer"><Organization /></ProtectedRoute>
                } />
                <Route path="/employer/departments/:departmentId" element={
                  <ProtectedRoute requiredRole="employer"><DepartmentDetail /></ProtectedRoute>
                } />
                <Route path="/employer/teams/:teamId" element={
                  <ProtectedRoute requiredRole="employer"><TeamDetail /></ProtectedRoute>
                } />
                <Route path="/employer/automations" element={
                  <ProtectedRoute requiredRole="employer"><Automations /></ProtectedRoute>
                } />
                <Route path="/employer/documents" element={
                  <ProtectedRoute requiredRole="employer"><Documents /></ProtectedRoute>
                } />
                <Route path="/employer/profile" element={
                  <ProtectedRoute requiredRole="employer"><EmployerProfile /></ProtectedRoute>
                } />

                {/* Super Admin — Agent Command Center */}
                <Route path="/employer/agents" element={
                  <SuperAdminRoute><AgentCommandCenter /></SuperAdminRoute>
                } />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
